import {
  getWorkspaceControlEndpoint
} from "./chunk-7RNMRPVZ.mjs";

// src/control-socket.ts
import net from "net";
import fs from "fs";
function serializeEnvelope(envelope) {
  return `${JSON.stringify(envelope)}
`;
}
function createControlLineParser(onEnvelope) {
  let buffer = "";
  return (chunk) => {
    buffer += chunk.toString();
    let newlineIndex = buffer.indexOf("\n");
    while (newlineIndex >= 0) {
      const rawLine = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);
      if (rawLine) {
        onEnvelope(JSON.parse(rawLine));
      }
      newlineIndex = buffer.indexOf("\n");
    }
  };
}
var AdhMuxControlClient = class {
  endpoint;
  socket = null;
  waiters = /* @__PURE__ */ new Map();
  eventListeners = /* @__PURE__ */ new Set();
  constructor(workspaceName) {
    this.endpoint = getWorkspaceControlEndpoint(workspaceName);
  }
  async connect() {
    if (this.socket && !this.socket.destroyed) return;
    const socket = net.createConnection(this.endpoint.path);
    this.socket = socket;
    socket.on("data", createControlLineParser((envelope) => {
      if (envelope.kind === "response") {
        const waiter = this.waiters.get(envelope.requestId);
        if (!waiter) return;
        this.waiters.delete(envelope.requestId);
        waiter.resolve(envelope.response);
        return;
      }
      if (envelope.kind === "event") {
        for (const listener of this.eventListeners) listener(envelope.event);
      }
    }));
    socket.on("error", (error) => {
      for (const waiter of this.waiters.values()) {
        waiter.reject(error);
      }
      this.waiters.clear();
    });
    await new Promise((resolve, reject) => {
      socket.once("connect", () => resolve());
      socket.once("error", reject);
    });
  }
  async request(request) {
    await this.connect();
    if (!this.socket) throw new Error("adhmux control socket unavailable");
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const envelope = { kind: "request", requestId, request };
    const response = await new Promise((resolve, reject) => {
      this.waiters.set(requestId, { resolve, reject });
      this.socket?.write(serializeEnvelope(envelope));
    });
    return response;
  }
  onEvent(listener) {
    this.eventListeners.add(listener);
    return () => {
      this.eventListeners.delete(listener);
    };
  }
  async close() {
    if (!this.socket) return;
    const socket = this.socket;
    this.socket = null;
    for (const waiter of this.waiters.values()) {
      waiter.reject(new Error("adhmux control client closed"));
    }
    this.waiters.clear();
    await new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      socket.once("close", done);
      socket.end();
      socket.destroy();
      setTimeout(done, 50);
    });
  }
};
async function withAdhMuxControlClient(workspaceName, fn) {
  const client = new AdhMuxControlClient(workspaceName);
  try {
    return await fn(client);
  } finally {
    await client.close();
  }
}
var AdhMuxControlServer = class {
  endpoint;
  sockets = /* @__PURE__ */ new Set();
  server;
  constructor(workspaceName, handle) {
    this.endpoint = getWorkspaceControlEndpoint(workspaceName);
    if (this.endpoint.kind === "unix" && fs.existsSync(this.endpoint.path)) {
      fs.unlinkSync(this.endpoint.path);
    }
    this.server = net.createServer((socket) => {
      this.sockets.add(socket);
      socket.on("close", () => {
        this.sockets.delete(socket);
      });
      socket.on("data", createControlLineParser(async (envelope) => {
        if (envelope.kind !== "request") return;
        const response = await handle(envelope.request).catch((error) => ({
          success: false,
          error: error?.message || String(error)
        }));
        socket.write(
          serializeEnvelope({
            kind: "response",
            requestId: envelope.requestId,
            response
          })
        );
      }));
    });
    this.server.listen(this.endpoint.path);
    this.server.on("close", () => {
      if (this.endpoint.kind === "unix" && fs.existsSync(this.endpoint.path)) {
        fs.unlinkSync(this.endpoint.path);
      }
    });
  }
  broadcast(event) {
    const envelope = serializeEnvelope({ kind: "event", event });
    for (const socket of this.sockets) {
      if (!socket.destroyed) socket.write(envelope);
    }
  }
  close() {
    this.server.close();
  }
};
function createAdhMuxControlServer(workspaceName, handle) {
  return new AdhMuxControlServer(workspaceName, handle);
}

export {
  AdhMuxControlClient,
  withAdhMuxControlClient,
  AdhMuxControlServer,
  createAdhMuxControlServer
};
